import { useContext } from 'react';
import { HelpersContext } from 'react-calendar-timeline';
import { DropTarget } from 'react-dnd';
import moment from 'moment';
import { DropTargetConnector } from 'react-dnd/lib/decorators/interfaces';
import { DropTargetMonitor } from 'react-dnd/lib/interfaces';
import { observer } from 'mobx-react';
import {
  addNewTimelineItemOnDrop,
  getTimelineDropTarget,
  TimelineAcademicCalendarIndication,
  TimelineDraggableSlot,
  TimelineItemType,
  TimelineRowProps,
  TimelineTimeSlotConfigurations,
} from '@modules/timetable';
import { rootStore, useMst } from '@models/Root';
import { TimelineDragObject } from '@app-types/timetable';
import dynamic from 'next/dynamic';

const GroupRow = dynamic(() => import('react-calendar-timeline').then((module) => module.GroupRow), {
  ssr: false,
});
const RowItems = dynamic(() => import('react-calendar-timeline').then((module) => module.RowItems), {
  ssr: false,
});

const TimelineRowComponent = observer(
  ({ draggableSlots, connectDropTarget, group, academicCalendarItems }: TimelineRowProps) => {
    const { getLeftOffsetFromDate } = useContext<HelpersContext>(HelpersContext);
    const { timeSlotConfigurations } = useMst().timetable;

    return connectDropTarget(
      <div
        style={{
          height: 90,
        }}
      >
        {/* @ts-ignore */}
        <GroupRow groupHeight={70}>
          <div className="group-row">
            <RowItems />
            {academicCalendarItems?.map((academicCalendarItem) => {
              // Academic calendar indication
              const leftOffset = getLeftOffsetFromDate(
                moment(new Date(academicCalendarItem.startDate)).startOf('day').toDate(),
              );
              const rightOffset = getLeftOffsetFromDate(
                moment(new Date(academicCalendarItem.endDate)).endOf('day').toDate(),
              );

              return (
                <TimelineAcademicCalendarIndication
                  key={academicCalendarItem.id}
                  academicCalendarItem={academicCalendarItem}
                  leftOffset={leftOffset}
                  rightOffset={rightOffset}
                />
              );
            })}
            {draggableSlots.map((slot) => {
              // Academic calendar draggable slots
              const leftOffset = getLeftOffsetFromDate(slot.startTime.toDate());
              const rightOffset = getLeftOffsetFromDate(slot.endTime.toDate());
              return (
                <TimelineDraggableSlot
                  key={`${group.id}-${slot.startTime.unix().toString()}`}
                  slot={slot}
                  group={group}
                  leftOffset={leftOffset}
                  rightOffset={rightOffset}
                />
              );
            })}

            {timeSlotConfigurations?.map((timeSlot) => {
              // Academic calendar time slot configurations
              const leftOffset = getLeftOffsetFromDate(timeSlot.startTime?.toDate());
              const rightOffset = getLeftOffsetFromDate(timeSlot.endTime?.toDate());
              const academicCalendarItem = academicCalendarItems?.find((item) => item.type === 'academic');
              return timeSlot?.startTime?.isSameOrAfter(moment(new Date(academicCalendarItem?.startDate))) &&
                timeSlot?.endTime.isSameOrBefore(moment(new Date(academicCalendarItem?.endDate))) ? (
                <TimelineTimeSlotConfigurations
                  key={`pre-slot-${group.id}-${timeSlot.startTime?.unix().toString()}`}
                  timeSlot={timeSlot}
                  leftOffset={leftOffset}
                  rightOffset={rightOffset}
                  group={group}
                />
              ) : (
                <></>
              );
            })}
          </div>
        </GroupRow>
      </div>,
    );
  },
);

const timelineDropSpec = {
  drop: (props: TimelineRowProps, monitor: DropTargetMonitor) => {
    const { selectedClassId, group, timelineItems, setTimelineItems, timetableSlotCounts, selectedLocation, addToast } =
      props;
    const item: TimelineDragObject = monitor.getItem();
    const dropPosition = monitor.getClientOffset();

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    rootStore.timetable.setDragHoverTime(null);

    if (dropPosition) {
      const dropTarget = getTimelineDropTarget({
        x: dropPosition.x,
        y: dropPosition.y,
      });

      if (dropTarget) {
        addNewTimelineItemOnDrop({
          dropTarget,
          item,
          group,
          selectedLocation,
          selectedClassId,
          timelineItems,
          timetableSlotCounts,
          setTimelineItems,
          addToast,
        }).then();
      }
    }
  },
  hover: (props: TimelineRowProps, monitor: DropTargetMonitor) => {
    const dropPosition = monitor.getClientOffset();
    let dropTarget: Element | null;
    const item: TimelineItemType = monitor.getItem();

    if (dropPosition) {
      dropTarget = document.elementFromPoint(dropPosition.x, dropPosition.y);

      if (dropTarget) {
        const startTime = moment.unix(parseInt(dropTarget.id, 10));
        const endTime = startTime.clone().add(item.duration, 'h');

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        rootStore.timetable.setDragHoverTime(`${startTime.format('h:mm a')} - ${endTime.format('h:mm a')}`);
      }
    }
  },
};

const collect = (connect: DropTargetConnector) => {
  return {
    connectDropTarget: connect.dropTarget(),
  };
};

export const TimelineRow = DropTarget(
  (props: TimelineRowProps) => {
    return props.group.type;
  },
  timelineDropSpec,
  collect,
)(TimelineRowComponent);
